import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PublicLayout } from "@/components/PublicLayout";
import { CondoForm } from "@/components/LeadForms";
import { api, mediaUrl } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { CheckCircle2, ShieldCheck, Monitor, Ruler, Clock, MapPin, BookOpen, ArrowRight, FileText } from "lucide-react";

const perks = [
  { icon: ShieldCheck, t: "Trasparenza sui conti", d: "Contabilità leggibile, rendiconti puntuali e nessun costo nascosto." },
  { icon: Monitor, t: "Portale per i condòmini", d: "Bilanci, documenti e comunicazioni online in ogni momento." },
  { icon: Ruler, t: "Occhio tecnico", d: "Siamo uno studio tecnico: manutenzioni, lavori e pratiche edilizie con competenza diretta." },
  { icon: Clock, t: "Presenti quando serve", d: "Guasti e imprevisti seguiti con tempestività, con un referente chiaro." },
];

function LocalAdminPage({ cfg }) {
  const { data: allPosts = [] } = useQuery({ queryKey: ["posts"], queryFn: async () => (await api.get("/posts")).data });
  const guides = allPosts.filter((p) => p.category === "amministrazioni").slice(0, 3);

  useSeo({
    title: `Amministratore di Condominio a ${cfg.name} | Edilstudio`,
    description: cfg.metaDesc,
    canonicalPath: cfg.path,
    image: cfg.img,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "serviceType": "Amministratore di condominio",
          "name": `Amministratore di condominio a ${cfg.name}`,
          "areaServed": { "@type": "City", "name": cfg.name },
          "provider": {
            "@type": "RealEstateAgent",
            "name": "Edilstudio Immobiliare",
            "telephone": "+390555277501",
            "email": "info@edilstudiofi.it",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Via Salvador Allende 14/c",
              "addressLocality": "Scandicci",
              "addressRegion": "FI",
              "postalCode": "50018",
              "addressCountry": "IT",
            },
          },
        },
        {
          "@type": "FAQPage",
          "mainEntity": cfg.faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
        },
      ],
    },
  });

  return (
    <PublicLayout>
      {/* HERO + FORM */}
      <section className="grid lg:grid-cols-2" data-testid="local-hero">
        <div className="bg-[#1B2A41] text-white px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          <p className="eyebrow text-[#F5A623]">Amministrazioni condominiali · {cfg.name}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-tight">Amministratore di condominio a {cfg.name}</h1>
          <p className="text-white/80 mt-5 leading-relaxed max-w-md">{cfg.lead}</p>
          <ul className="mt-8 space-y-4">
            {cfg.bullets.map((t) => (
              <li key={t} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#F5A623] mt-0.5 shrink-0" /><span className="text-white/85">{t}</span></li>
            ))}
          </ul>
        </div>
        <div className="px-6 sm:px-10 lg:px-16 py-16 lg:py-24 bg-[#F7FAFC]">
          <div className="bg-white rounded-2xl border border-[#DCE6EF] p-6 sm:p-8">
            <h2 className="font-display text-2xl font-bold text-[#1B2A41]">Richiedi un preventivo per il tuo condominio</h2>
            <p className="text-sm text-[#566476] mt-1 mb-6">Raccontaci la situazione: ti rispondiamo entro 24h con un confronto gratuito e un'offerta su misura.</p>
            <CondoForm />
          </div>
        </div>
      </section>

      {/* INTRO LOCALE + IMMAGINE + ZONE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24" data-testid="local-intro">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#EEF4F9] flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#2472C8]" /></div>
              <p className="eyebrow text-[#2472C8]">Vicini al tuo condominio</p>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-4 leading-tight">Una gestione che conosce {cfg.name}</h2>
            <div className="mt-6 space-y-4 text-[#43526A] leading-relaxed text-[17px]">
              {cfg.intro.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="mt-7">
              <p className="text-sm font-semibold text-[#1B2A41] mb-3">Seguiamo condomini nelle zone di:</p>
              <div className="flex flex-wrap gap-2">
                {cfg.zones.map((z) => (
                  <span key={z} className="text-sm bg-[#EEF4F9] text-[#2472C8] font-medium px-3 py-1.5 rounded-full">{z}</span>
                ))}
              </div>
            </div>
          </div>
          <div>
            <img src={cfg.img} alt={`Condomini a ${cfg.name} amministrati da Edilstudio`} loading="lazy" className="w-full aspect-[4/3] object-cover rounded-2xl shadow-lg" />
            {cfg.credit && <p className="text-[11px] text-[#8695A6] mt-2">{cfg.credit}</p>}
          </div>
        </div>
      </section>

      {/* APPROCCIO */}
      <section className="bg-[#EEF4F9] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#2472C8]">Il nostro approccio</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2 max-w-2xl">Trasparente, tecnica e vicina</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {perks.map((p) => (
              <div key={p.t} className="bg-white rounded-2xl border border-[#DCE6EF] p-6">
                <div className="w-12 h-12 rounded-xl bg-[#EEF4F9] flex items-center justify-center"><p.icon className="w-6 h-6 text-[#2472C8]" /></div>
                <h3 className="font-heading font-bold text-[#1B2A41] mt-4">{p.t}</h3>
                <p className="text-sm text-[#566476] mt-2 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/tariffario-condominio" className="inline-flex items-center gap-1.5 text-[#2472C8] font-semibold text-sm hover:gap-2.5 transition-all"><FileText className="w-4 h-4" /> Consulta il tariffario</Link>
            <span className="text-[#CBD7E2]">·</span>
            <Link to="/cambio-amministratore-condominio" className="inline-flex items-center gap-1.5 text-[#2472C8] font-semibold text-sm hover:gap-2.5 transition-all">Vuoi cambiare amministratore? →</Link>
          </div>
        </div>
      </section>

      {/* GUIDE */}
      {guides.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
          <p className="eyebrow text-[#2472C8]">Approfondimenti</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Guide sulla gestione condominiale</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {guides.map((g) => (
              <Link key={g.slug} to={`/blog/${g.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-[#DCE6EF] hover:shadow-xl transition-shadow">
                <div className="aspect-video overflow-hidden"><img src={mediaUrl(g.cover_image)} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6">
                  <div className="flex items-center gap-1.5 text-xs font-mono text-[#2472C8]"><BookOpen className="w-3.5 h-3.5" /> Blog</div>
                  <h3 className="font-heading font-semibold text-[#1B2A41] mt-2 leading-snug group-hover:text-[#2472C8] transition-colors line-clamp-3">{g.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-[#EEF4F9] py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#2472C8]">Domande frequenti</p>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#1B2A41] mt-2">Amministratore a {cfg.name}: i tuoi dubbi</h2>
          <div className="mt-8 space-y-3">
            {cfg.faqs.map((f, i) => (
              <details key={i} className="group bg-white rounded-2xl border border-[#DCE6EF] p-5 open:shadow-md transition-shadow">
                <summary className="flex items-center justify-between cursor-pointer font-heading font-semibold text-[#1B2A41] list-none">
                  <span>{f.q}</span>
                  <ArrowRight className="w-5 h-5 text-[#8695A6] shrink-0 transition-transform group-open:rotate-90" />
                </summary>
                <p className="text-[#566476] mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1B2A41] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl lg:text-3xl font-bold">Cerchi un amministratore a {cfg.name}? Parliamone.</h2>
            <p className="text-white/75 mt-2">Un confronto gratuito e senza impegno per il tuo condominio.</p>
          </div>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="bg-[#F5A623] hover:bg-[#E0961C] text-[#1B2A41] font-bold px-7 py-4 rounded-full flex items-center gap-2 transition-colors whitespace-nowrap">
            Richiedi il preventivo <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}

const SCANDICCI = {
  name: "Scandicci",
  path: "/amministratore-condominio-scandicci",
  img: "/img/amministratore-scandicci.jpg",
  credit: "Foto: Francileo07 – Centro culturale, Piazza della Resistenza, Scandicci (Wikimedia Commons, CC BY-SA 4.0)",
  metaDesc: "Amministratore di condominio a Scandicci: studio tecnico con sede in città, gestione trasparente, portale condòmini e preventivo gratuito. Contabilità chiara e manutenzioni seguite.",
  lead: "Cerchi un amministratore di condominio a Scandicci serio e trasparente? Il nostro studio ha sede proprio a Scandicci, in Via Salvador Allende: conosciamo il territorio, gli edifici e le esigenze dei condòmini della zona.",
  bullets: ["Studio con sede a Scandicci, sempre raggiungibile", "Contabilità trasparente e portale online", "Occhio tecnico su manutenzioni e lavori", "Preventivo gratuito e senza impegno"],
  intro: [
    "Amministrare un condominio a Scandicci significa conoscere da vicino il territorio: dalle palazzine del centro alle zone residenziali di Vingone, Casellina e San Giusto, ogni edificio ha le sue caratteristiche e le sue necessità. Avere un amministratore con studio in città vuol dire risposte rapide e sopralluoghi tempestivi.",
    "Come studio tecnico uniamo la gestione contabile e amministrativa a una competenza diretta sugli aspetti edilizi: valutiamo lo stato dello stabile, programmiamo le manutenzioni e seguiamo pratiche e bonus con precisione. Il tutto con conti chiari e un portale dove i condòmini trovano documenti e bilanci quando vogliono.",
  ],
  zones: ["Centro", "Vingone", "Casellina", "San Giusto", "Le Bagnese", "Badia a Settimo", "San Vincenzo a Torri", "Olmo"],
  faqs: [
    { q: "Avete sede a Scandicci?", a: "Sì, il nostro studio si trova a Scandicci in Via Salvador Allende 14/c. Essere sul territorio ci permette di effettuare sopralluoghi rapidi e di essere facilmente raggiungibili dai condòmini." },
    { q: "Quanto costa un amministratore di condominio a Scandicci?", a: "Il compenso di gestione ordinaria si colloca indicativamente tra 80 e 110 euro per unità l'anno, oltre alle attività contabilizzate a misura in base all'effettiva esecuzione. Trovi tutte le voci nel nostro tariffario trasparente." },
    { q: "Gestite anche condomini piccoli?", a: "Sì, seguiamo condomini di ogni dimensione, dalle piccole palazzine agli stabili più grandi, con lo stesso livello di attenzione e trasparenza." },
    { q: "Posso cambiare amministratore in corso d'anno?", a: "Certo: l'assemblea può revocare e nominare un nuovo amministratore in qualsiasi momento. Ti spieghiamo come nella nostra guida al cambio amministratore." },
  ],
};

const FIRENZE = {
  name: "Firenze",
  path: "/amministratore-condominio-firenze",
  img: "/img/amministratore-firenze.jpg",
  metaDesc: "Amministratore di condominio a Firenze: studio tecnico con gestione trasparente, portale condòmini e preventivo gratuito. Competenza su palazzi storici, manutenzioni e pratiche edilizie.",
  lead: "Cerchi un amministratore di condominio a Firenze competente e trasparente? Operiamo a Firenze e nell'area fiorentina con una gestione puntuale, contabilità chiara e uno sguardo tecnico su ogni edificio.",
  bullets: ["Operiamo a Firenze e nell'area fiorentina", "Contabilità trasparente e portale online", "Competenza su palazzi storici e pratiche edilizie", "Preventivo gratuito e senza impegno"],
  intro: [
    "Firenze è fatta di condomini molto diversi tra loro: dai palazzi storici del centro agli edifici residenziali di Campo di Marte, Rifredi, Novoli e Gavinana. Ognuno richiede attenzioni specifiche, soprattutto quando ci sono vincoli o interventi di manutenzione importanti.",
    "Come studio tecnico uniamo la gestione contabile e amministrativa a una competenza diretta sugli aspetti edilizi: valutiamo lo stato dello stabile, programmiamo le manutenzioni e seguiamo pratiche e bonus con precisione. Il tutto con conti chiari e un portale dove i condòmini trovano documenti e bilanci quando vogliono.",
  ],
  zones: ["Centro storico", "Campo di Marte", "Rifredi", "Novoli", "Gavinana", "Isolotto", "Le Cure", "Oltrarno", "Coverciano"],
  faqs: [
    { q: "Operate in tutta Firenze?", a: "Sì, gestiamo condomini a Firenze città e nell'area fiorentina. Il nostro studio è a Scandicci, a pochi minuti da Firenze, il che ci consente sopralluoghi rapidi e una presenza costante." },
    { q: "Quanto costa un amministratore di condominio a Firenze?", a: "Il compenso di gestione ordinaria si colloca indicativamente tra 80 e 110 euro per unità l'anno, oltre alle attività contabilizzate a misura in base all'effettiva esecuzione. Trovi tutte le voci nel nostro tariffario trasparente." },
    { q: "Gestite anche palazzi storici o con vincoli?", a: "Sì. Essendo uno studio tecnico, sappiamo gestire edifici con vincoli, pratiche edilizie e manutenzioni straordinarie con la competenza necessaria." },
    { q: "Posso cambiare amministratore in corso d'anno?", a: "Certo: l'assemblea può revocare e nominare un nuovo amministratore in qualsiasi momento. Ti spieghiamo come nella nostra guida al cambio amministratore." },
  ],
};

export function AmministratoreScandicci() { return <LocalAdminPage cfg={SCANDICCI} />; }
export function AmministratoreFirenze() { return <LocalAdminPage cfg={FIRENZE} />; }
